import { useRequiredContext } from "@redotech/react-util/context";
import { setLocalStorageWithExpiry } from "@redotech/redo-web/utils/local-storage-wrapper";
import { CONCIERGE_CONVERSATION_LOCAL_STORAGE_KEY } from "@redotech/redo-web/utils/shared-conf";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, memo, useCallback, useEffect, useMemo } from "react";
import { CONCIERGE_CONVERSATION_LOCAL_STORAGE_HOURS } from "../core/config";
import { WidgetView } from "../core/routes";
import {
  useConversation,
  useCurrentConversationId,
  usePreviousConversationId,
} from "../hooks/conversation-hooks";
import { useWidgetNavigation } from "../hooks/use-widget-navigation";
import {
  createTempConversation,
  getNewConversationType,
  TEMP_CONVERSATION_ID,
} from "../utils/add-message-utils";
import { TeamInfoContext } from "./team-info";

export enum ConversationType {
  AI = "AI",
  LIVE = "LIVE",
  CHAT_FLOW = "CHAT_FLOW",
  EMAIL = "EMAIL",
}

interface ActiveConversationContext {
  conversationId: string | undefined;
  conversation: ReturnType<typeof useConversation>["conversation"];
  conversationType: ConversationType;
  isLoading: boolean;
  error: Error | null;
  setActiveConversationId: (conversationId: string) => void;
  navigateToConversation: (conversationId: string) => void;
  resetActiveConversation: () => void;
  setTempConversation: (
    conversation: ReturnType<typeof createTempConversation>,
  ) => void;
}

const ActiveConversationContext = createContext<
  ActiveConversationContext | undefined
>(undefined);

export const ActiveConversationProvider = memo(
  function ActiveConversationProvider({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const [localStorageConversationId, setLocalStorageConversationId] =
      useCurrentConversationId();
    const [, setPreviousConversationId] = usePreviousConversationId();
    const { navigate } = useWidgetNavigation();
    const queryClient = useQueryClient();
    const teamInfo = useRequiredContext(TeamInfoContext);

    const activeConversationId = localStorageConversationId;

    const { conversation, isLoading, error } = useConversation(
      activeConversationId || "",
    );

    const conversationType: ConversationType = useMemo(() => {
      return getNewConversationType(teamInfo.conciergeLiveChat, conversation);
    }, [teamInfo.conciergeLiveChat, conversation]);

    const ensureConversationInitialized = useCallback(
      (currentConversationId?: string) => {
        // Only initialize if we don't have a real conversation
        if (
          !currentConversationId ||
          currentConversationId === TEMP_CONVERSATION_ID
        ) {
          // Since we are using undefined for the conversation it will just return the default type for out settings
          const conversationType = getNewConversationType(
            teamInfo.conciergeLiveChat,
            undefined,
          );
          const tempConversation = createTempConversation(
            [],
            conversationType,
            teamInfo,
          );

          queryClient.setQueryData(
            ["conversation", TEMP_CONVERSATION_ID],
            tempConversation,
          );
        }
      },
      [queryClient, teamInfo],
    );

    useEffect(() => {
      ensureConversationInitialized(activeConversationId);

      if (
        activeConversationId &&
        activeConversationId !== TEMP_CONVERSATION_ID
      ) {
        setLocalStorageWithExpiry(
          CONCIERGE_CONVERSATION_LOCAL_STORAGE_KEY,
          activeConversationId,
          CONCIERGE_CONVERSATION_LOCAL_STORAGE_HOURS,
        );
      }
    }, [activeConversationId, ensureConversationInitialized]);

    const setActiveConversationId = useCallback(
      (conversationId: string) => {
        setLocalStorageConversationId(conversationId);

        // If setting a real conversation ID and the URL doesn't match,
        // navigate to the specific conversation
        if (conversationId) {
          navigate({ to: WidgetView.CHAT });
        }
      },
      [setLocalStorageConversationId, navigate],
    );

    const navigateToConversation = useCallback(
      (conversationId: string) => {
        setLocalStorageConversationId(conversationId);
        navigate({ to: WidgetView.CHAT });
      },
      [navigate, setLocalStorageConversationId],
    );

    const resetActiveConversation = useCallback(() => {
      if (
        localStorageConversationId &&
        localStorageConversationId !== TEMP_CONVERSATION_ID
      ) {
        setPreviousConversationId(localStorageConversationId);
      }

      // This will return the default conversation type since conversation is undefined
      const newConversationType = getNewConversationType(
        teamInfo.conciergeLiveChat,
        undefined,
      );
      const tempConversation = createTempConversation(
        [],
        newConversationType,
        teamInfo,
      );

      queryClient.setQueryData(
        ["conversation", TEMP_CONVERSATION_ID],
        tempConversation,
      );

      setLocalStorageConversationId(TEMP_CONVERSATION_ID);
    }, [
      localStorageConversationId,
      setPreviousConversationId,
      queryClient,
      setLocalStorageConversationId,
      teamInfo,
    ]);

    const setTempConversation = useCallback(
      (conversation: ReturnType<typeof createTempConversation>) => {
        if (
          localStorageConversationId &&
          localStorageConversationId !== TEMP_CONVERSATION_ID
        ) {
          setPreviousConversationId(localStorageConversationId);
        }

        queryClient.setQueryData(
          ["conversation", TEMP_CONVERSATION_ID],
          conversation,
        );

        setLocalStorageConversationId(TEMP_CONVERSATION_ID);
      },
      [
        localStorageConversationId,
        setPreviousConversationId,
        queryClient,
        setLocalStorageConversationId,
      ],
    );

    const contextValue: ActiveConversationContext = {
      conversationId: activeConversationId,
      conversation,
      conversationType,
      isLoading,
      error,
      setActiveConversationId,
      navigateToConversation,
      resetActiveConversation,
      setTempConversation,
    };

    return (
      <ActiveConversationContext.Provider value={contextValue}>
        {children}
      </ActiveConversationContext.Provider>
    );
  },
);

export function useActiveConversation() {
  return useRequiredContext(ActiveConversationContext);
}
